import EmberFlame from './EmberFlame';
import EmberLockup from './EmberLockup';
import EmberWordmark from './EmberWordmark';
import { clearCookieConsent } from './cookie-consent';
import { SOCIAL_PROFILE_URLS } from './site-content-runtime';
import StoreBadges from './StoreBadges';
import './site-chrome.css';

const SOCIAL_LABELS: Record<string, string> = {
  'facebook.com': 'Facebook',
  'instagram.com': 'Instagram',
  'youtube.com': 'YouTube',
  'tiktok.com': 'TikTok',
};

function socialLabel(profileUrl: string): string {
  try {
    const host = new URL(profileUrl).hostname.replace(/^www\./, '');
    return SOCIAL_LABELS[host] || 'Social profile';
  } catch {
    return 'Social profile';
  }
}

/**
 * The site footer. One component in the whole product: the homepage renders
 * it, and so do the footer's own narrative pages (/how-ember-works,
 * /real-human-experiences, /privacy-and-sharing, /about, /community-guidelines
 * — the 2026-08-30 footer redesign; content source is the footer 定稿 in
 * ember-brain v3.0 §4.15); every other surface opens with the page-level Back
 * control and ends with its own content.
 *
 * Composition (v4.0, 2026-09-04): the brand statement (worldview + the rule),
 * then two content columns (Understand / Ember), then the legal row, social
 * profiles, and the meta line. The Explore column left with the discovery
 * surfaces (§0.3: no browsing, no search, no questions). Understand carries the
 * use-case hub as of 2026-09-06 — see the note beside it; the Blog went back to
 * the homepage nav the same day rather than here.
 *
 * Two of its rows are compliance surfaces, which is why they live here rather
 * than in page copy:
 *  - "Cookie preferences" is the standing way out of ad-pixel consent. A choice
 *    you cannot revisit is not an opt-out, so every page that carries the
 *    footer keeps the door open. Forgetting the stored choice and landing on
 *    the homepage brings the banner back, which is where both buttons live.
 *  - The A2P messaging disclosure lives in the Terms, which stay one link away.
 */
export default function SiteFooter({
  /**
   * 'auto' follows the visitor's OS. 'light'/'dark' pin it — needed by pages
   * whose own CSS is unconditionally one or the other, where following the OS
   * means the footer disagrees with the page half the time.
   */
  theme = 'auto',
  /**
   * The page knows the visitor is signed in (the homepage, from its session).
   * Sign in is for the signed-out: shown to a signed-in person it sent them
   * to /auth and straight back to the page they were on, which read as a
   * link that did nothing (prod 2026-10-19: one person tapped it five times).
   * The authored pages render the same for everyone and leave it as it was.
   */
  signedIn = false,
}: { theme?: 'auto' | 'light' | 'dark'; signedIn?: boolean } = {}) {
  const chrome = `eh-chrome${theme === 'light' ? ' eh-chrome-light' : ''}${theme === 'dark' ? ' eh-chrome-dark' : ''}`;

  return (
    <footer className={`eh-footer eh-footer-slim ${chrome}`}>
      <div className="eh-shell">
        <div className="eh-footer-brand">
          <a className="eh-wordmark eh-footer-wordmark" href="/" aria-label="Ember home" data-log="footer_logo">
            <EmberLockup className="eh-lockup eh-footer-lockup" />
            <span className="eh-footer-mobile-lockup" aria-hidden="true">
              <EmberFlame className="eh-footer-mobile-flame" />
              <EmberWordmark className="eh-footer-mobile-wordmark" />
            </span>
          </a>
          {/* The brand line (positioning v2, 2026-10-28): talk it out, get
              understood, meet people who get you — the path in that order. */}
          <p className="eh-footer-brand-title">Talk it out. Get understood. Meet people who get you.</p>
          {/* The apps (2026-10-26 Riley): both stores' badges on every page that carries the footer. */}
          <StoreBadges discovery="footer" className="eh-footer-apps" />
          {/* Featured on There's An AI For That (2026-11-02): the badge is the listing's
              verification on TAAFT (its v= token) and a link to Ember's page there. */}
          <a
            className="eh-footer-taaft"
            href="https://theresanaiforthat.com/ai/ember-talk-it-out/?ref=featured&v=13701478"
            target="_blank"
            rel="nofollow noopener"
            data-log="footer_taaft"
          >
            <img width="180" src="https://media.theresanaiforthat.com/featured-on-taaft.png?width=600" alt="Featured on There's An AI For That" />
          </a>
        </div>
        {/* v4.0: no discovery column — Questions, Themes and Search are gone
            from every public surface. 2026-09-29: Comparison joins as the
            third, so the articles that answer "how is this different from the
            thing I already use" have somewhere on the site to be linked from.
            The column lists them outright rather than revealing them on hover:
            a phone has no hover, and a link a crawler cannot see is a link
            that does not count. */}
        <div className="eh-footer-cols eh-footer-slim-explore">
          <section>
            <h2>Understand</h2>
            <nav className="eh-footer-explore" aria-label="Understand Ember">
              <a href="/how-ember-works" data-log="footer_explore_how">
                How Ember works
              </a>
              <a href="/real-human-experiences" data-log="footer_explore_real">
                Why lived experience matters
              </a>
              <a href="/privacy-and-sharing" data-log="footer_explore_privacy_sharing">
                Privacy &amp; sharing
              </a>
              <a href="/about" data-log="footer_explore_about">
                About Ember
              </a>
              {/* The use-case hub. It left the footer with the 2026-08-30
                  redesign and stayed live but unlinked, which made it and the
                  five /for/<situation> pages under it orphans: in the sitemap,
                  reachable by search, and reached by nothing on the site. One
                  line here is the whole of their inbound linking (2026-09-06
                  拍板). The /for/ pages stay off the footer — the hub links
                  them, which is what a hub is for. */}
              <a href="/ways-to-use-ember" data-log="footer_explore_use_cases">
                Ways to use Ember
              </a>
            </nav>
          </section>
          <section>
            <h2>Comparison</h2>
            <nav className="eh-footer-explore" aria-label="Ember compared with other tools">
              <a href="/compare" data-log="footer_compare_index">
                How Ember compares
              </a>
              <a href="/compare/ember-vs-chatgpt" data-log="footer_compare_chatgpt">
                Ember vs ChatGPT
              </a>
              <a href="/compare/ember-vs-journaling-apps" data-log="footer_compare_journaling">
                Ember vs journaling apps
              </a>
            </nav>
          </section>
          <section>
            <h2>Ember</h2>
            <nav className="eh-footer-explore" aria-label="Use Ember">
              {/* The conversation entrance as a link — /talk offers the same
                  two doors (message / voice) as every in-app talk sheet. */}
              <a href="/talk" data-log="footer_explore_talk">
                Type or talk
              </a>
              {signedIn ? null : (
                <a href="/auth" data-log="footer_explore_signin">
                  Sign in
                </a>
              )}
              {/* The message board to the team (/feedback): reaches signed-out
                  visitors, which the account menu's copy of this entry cannot.
                  The retired contact-email row stays retired — the email lives
                  on that page. */}
              <a href="/feedback" data-log="footer_feedback">
                Feedback
              </a>
            </nav>
          </section>
        </div>
        <nav className="eh-footer-primary-nav eh-footer-legal" aria-label="Legal">
          <a href="/privacy" data-log="footer_privacy">Privacy</a>
          <a href="/terms" data-log="footer_terms">Terms</a>
          <a href="/community-guidelines" data-log="footer_explore_guidelines">Guidelines</a>
          <a href="/safety" data-log="footer_safety">Safety</a>
          <a href="/child-safety" data-log="footer_child_safety">Child safety</a>
          <button
            type="button"
            className="eh-footer-link-button"
            data-log="footer_cookie_preferences"
            onClick={() => {
              // The consent banner is raised on the homepage, so land there:
              // on the homepage this is a plain reload, and from the other
              // footer-carrying pages it reaches the choice instead of
              // reloading a page with no banner to show.
              clearCookieConsent();
              window.location.href = '/';
            }}
          >
            Cookie preferences
          </button>
        </nav>
        <nav className="eh-footer-social" aria-label="Official Ember social media">
          {SOCIAL_PROFILE_URLS.map((profileUrl) => {
            const label = socialLabel(profileUrl);
            return (
              <a
                key={profileUrl}
                href={profileUrl}
                target="_blank"
                rel="me noopener noreferrer"
                data-log={`footer_social_${label.toLowerCase()}`}
              >
                {label}
              </a>
            );
          })}
        </nav>
        <div className="eh-footer-meta">
          <span>© 2026 Oriscen Inc.</span>
          <span>Ember is in beta.</span>
        </div>
      </div>
    </footer>
  );
}
